import type { FileDataSchema, MeshType } from '../../../data/data-interfaces.ts';
import { sendEv } from '../../../data/events.ts';
import { DeprecatedEl } from '../../../util/deprecatedEl.ts';
import { ExportBuilder } from '../lib/exportBuilder.ts';
import { asApp, asHeader } from '../../../util/singletons.ts';
import { internalData } from '../../../store/store.ts';
import '../../../components/base/as-op-status.ts';
import type { AsOpStatus } from '../../../components/base/as-op-status.ts';
import '../../../components/inputs/buttons/as-button.ts';
import '../../../components/inputs/buttons/as-icon-button.ts';
import '../../../components/inputs/as-checkbox.ts';
import '../../../components/inputs/as-dropdown.ts';
import '../../../components/inputs/as-string-input.ts';
import '../../../components/layout/as-accordion.ts';
import '../../../components/overlays/as-hovercard-wrapper.ts';
import '../../../components/overlays/as-hovercard.ts';
import '../../../components/parameters/as-param-group.ts';
import '../../../components/text-and-lang/as-text.ts';
import styleText from './as-export-group.css?raw';
import { DOWNLOAD_DISABLED_TOOLTIP, DOWNLOAD_DISABLED_TOOLTIP_DICT_KEY, wireDownloadButton, type DownloadWiring } from './downloadWiring.ts';

/**
 * <as-export-group>
 *
 * Drop-in export step: renders two <as-param-group> cards in light DOM so they
 * participate directly in the parent <as-step-panel>'s flex layout.
 *
 * Panels
 *   1. Export           — Save file / Transfer to GrabCAD / Send to other apps
 *   2. Export scope     — scope checkboxes (default: outputPart, workpiece, supports)
 *
 * The design preview card is handled by <as-export-preview-panel>, which should be
 * placed as a sibling before this element inside the parent <as-step-panel>.
 *
 * Builder API (call after element is connected)
 *   step.packageName = 'my-part'          — File name input + download + GrabCAD package-name
 *   step.saveFormats = [...]              — override default STL/3MF options
 *   step.exporter = builder               — explicit exporter; defaults to as-app.exporter
 *   step.addExportScope(item)             — add a scope checkbox; chainable
 *   step.addChildScope(parentKey, item)   — add an indented child under an existing scope; chainable
 *   step.removeExportScope(key)           — remove a scope checkbox by key; chainable
 *   step.hasKey(key)                      — returns true if the scope key is registered
 *   step.scopes[key]                      — live accessor: .checked, .visible, .fileCfgs, .label
 *   step.exportScopes[key]                — deprecated alias for step.scopes[key]
 *   step.setFromObject(config)            — bulk set (renamed from configure)
 *   step.includeStepFiles                 — the "Auxiliary files" STEP checkbox state
 *   step.reset()                          — reset all button statuses
 *   step.downloadButton / step.downloadStatus / step.transferButton
 *                                         — the Save file <as-button>, its <as-op-status>,
 *                                           and the "Send to other apps…" <as-button>
 *
 * Attributes
 *   no-default-scopes    — suppress the built-in outputPart / workpiece / supports checkboxes
 *   no-default-exporter  — disable auto-fallback to as-app.exporter
 *   no-transfer-button   — hide the "Send to other apps…" button
 *
 * STEP export is a single "Auxiliary files" setting instead of a per-scope
 * checkbox: a settings cog next to the GrabCAD transfer button opens a
 * hovercard with one STEP checkbox (also readable/writable as
 * step.includeStepFiles). When it's on, every checked scope also exports its
 * matching STEP files as dependencies. No extra wiring needed — the STEP file
 * configs are derived from each scope's own fileCfgs (one 'brep' entry per
 * trcadMesh/workpiece/outputPart entry, auto-fetched from as-configurator,
 * same fileName, attached to its own part). Pass { noStepChild: true } in a
 * scope's config to opt that one scope out of the derivation. The cog rides
 * with the transfer button: its row is hidden when no plugin button is visible
 * in the current env, so there's never a lone cog sitting on its own.
 *
 * Scope checkboxes bubble as-input-change with name = item key.
 *
 * Localization: the built-in copy (panel headings, button/tooltip labels, the
 * settings hovercard, and the default scope checkboxes) renders through
 * <as-text>/<as-button>/<as-accordion> with baked-in dict keys
 * (fileIO.export.* in sharedLocalisation.json), so it translates even without
 * a langFile. A scope added via addExportScope()/addChildScope() only
 * translates if you pass your own labelDictKey.
 */

export interface ExportScopeConfig {
  key: string;
  defaultLabelText: string;
  labelDictKey?: string;
  checked?: boolean;
  visible?: boolean;
  fileCfgs?: FileDataSchema[];
  children?: ExportScopeConfig[];
  /** Opt this scope out of the auxiliary STEP derivation (see includeStepFiles). Default: included. */
  noStepChild?: boolean;
}

export interface FormatOption {
  value: string;
  label: string;
}

export interface ExportGroupConfig {
  packageName?: string;
  scopeItems?: ExportScopeConfig[];
  saveFormats?: FormatOption[];
  exporter?: ExportBuilder;
}

/** Public type for a scope entry returned by scopes. */
export interface ExportScope {
  readonly key: string;
  defaultLabelText: string;
  checked: boolean;
  readonly disabled: boolean;
  visible: boolean;
  fileCfgs: FileDataSchema[];
  readonly el: HTMLElement | undefined;
}

class ExportScopeConfigAccessor implements ExportScope {
  readonly key: string;
  private _defaultLabelText: string;
  fileCfgs: FileDataSchema[];
  /** Excluded from the auxiliary STEP derivation when true (config's noStepChild). */
  noStepChild = false;
  private readonly _elRef: WeakRef<HTMLElement>;
  private _childKeyList: string[] = [];
  private _visible: boolean;
  private _onVisibilityChange: (() => void) | undefined;

  constructor(key: string, defaultLabelText: string, el: HTMLElement, fileCfgs?: FileDataSchema[], visible = true) {
    this.key = key;
    this._defaultLabelText = defaultLabelText;
    this.fileCfgs = fileCfgs ?? [];
    this._elRef = new WeakRef(el);
    this._visible = visible;
    if (!visible) el.style.display = 'none';
  }

  get defaultLabelText(): string { return this._defaultLabelText; }
  set defaultLabelText(v: string) {
    this._defaultLabelText = v;
    this._elRef.deref()?.setAttribute('label-default-text', v);
  }

  get checked(): boolean { return (this._elRef.deref() as any)?.checked ?? false; }
  set checked(v: boolean) { const el = this._elRef.deref(); if (el) (el as any).checked = v; }

  get disabled(): boolean { return this._elRef.deref()?.hasAttribute('disabled') ?? false; }

  get visible(): boolean { return this._visible; }
  set visible(v: boolean) {
    this._visible = v;
    const el = this._elRef.deref();
    if (el) el.style.display = v ? '' : 'none';
    this._onVisibilityChange?.();
  }

  get el(): HTMLElement | undefined { return this._elRef.deref(); }

  _setOnVisibilityChange(cb: () => void): void { this._onVisibilityChange = cb; }
  _setChildKeys(keys: string[]): void { this._childKeyList = [...keys]; }
  _addChildKey(key: string): void { this._childKeyList.push(key); }
  get _childKeys(): readonly string[] { return this._childKeyList; }
}

const SAVE_TOOLTIP = 'Save the selected models to your computer.';

const DEFAULT_FORMATS: FormatOption[] = [
  { value: 'stl', label: 'STL' },
  { value: '3mf', label: '3MF' },
];

const DEFAULT_SCOPES: ExportScopeConfig[] = [
  { key: 'trcadMesh', defaultLabelText: 'Output parts', labelDictKey: 'fileIO.export.scope.outputParts' },
  { key: 'workpiece', defaultLabelText: 'Workpieces', labelDictKey: 'fileIO.export.scope.workpieces' },
  // No noStepChild here: apps build their standard components from the
  // configurator like any other part (the vice, the gripper), so they have a
  // STEP counterpart and must follow the scope selection.
  { key: 'standard-components', defaultLabelText: 'Standard Components', labelDictKey: 'fileIO.export.scope.standardComponents' },
];

/** Categories whose entries have a meaningful STEP counterpart. */
const STEP_SOURCE_CATEGORIES = new Set(['trcadMesh', 'workpiece', 'outputPart']);

/**
 * Derives the auxiliary STEP fileCfgs from a scope's own fileCfgs, keyed by
 * scope entry (object identity) so a multi-part scope's STEP files end up
 * attached to the correct part instead of all being bundled onto the first one.
 * One 'brep' (auto-fetched as STEP) entry per mesh-like scope entry, same
 * fileName and the same finalize setting — the STEP is the same part in another
 * format, so it must be fetched the same way its mesh is.
 *
 * No beforeExport: the derived entry is added as a child of its own entry, so
 * it resolves immediately after it, with the configurator still in the state
 * that entry's own beforeExport left behind.
 */
function deriveStepFileCfgsByPart(parentFileCfgs: FileDataSchema[]): Map<FileDataSchema, FileDataSchema> {
  const map = new Map<FileDataSchema, FileDataSchema>();
  for (const cfg of parentFileCfgs) {
    if (STEP_SOURCE_CATEGORIES.has(cfg.category)) {
      map.set(cfg, {
        fileName: cfg.fileName,
        category: 'brep',
        ...(cfg.finalize !== undefined && { finalize: cfg.finalize }),
      });
    }
  }
  return map;
}

export class AsExportGroup extends HTMLElement {
  static readonly tag = 'as-export-group' as const;

  private _config: ExportGroupConfig = {};
  private _exporter: ExportBuilder | null = null;
  private _observer: MutationObserver | null = null;
  private _built = false;

  private _scopeGroupEl: HTMLElement | null = null;
  private _exportSettingsButton: HTMLElement | null = null;
  private _filenameInputEl: HTMLElement | null = null;
  private _formatDropdownEl: HTMLElement | null = null;
  private _downloadWiring: DownloadWiring | null = null;
  /** Wraps the Save file button + status + settings cog; hidden in the GrabCAD env. */
  private _saveRowEl: HTMLElement | null = null;
  /** The "Save file" <as-button>. */
  downloadButton: HTMLElement | null = null;
  /** The <as-op-status> showing download progress/result. */
  downloadStatus: HTMLElement | null = null;
  /** The "Send to other apps…" <as-button>. */
  transferButton: HTMLElement | null = null;
  private _gcBtnEntries: Array<{ el: HTMLElement; requiredEnv?: string; extraAttrs: string[] }> = [];
  private _actionsRowEl: HTMLElement | null = null;
  /** Row holding the GrabCAD-style plugin button(s) plus the auxiliary-files cog. */
  private _gcRowEl: HTMLElement | null = null;
  /** The auxiliary-files settings cog's hovercard wrapper — plugin buttons insert before it. */
  private _auxSettingsWrapper: HTMLElement | null = null;
  /** The STEP <as-checkbox> inside the auxiliary-files hovercard. */
  private _auxStepCheckboxEl: HTMLElement | null = null;
  private _scopeMap: Map<string, ExportScopeConfigAccessor> = new Map();
  private _nameUserEdited = false;
  private _activeProjectId: string | null = null;
  private _updatingName = false;
  private _removeProjectEffect: (() => void) | null = null;
  private _removeAppIdEffect: (() => void) | null = null;
  private _removeCanDownloadEffect: (() => void) | null = null;
  private _onResetBound = (): void => this._handleSessionReset();
  private _onDescriptorRegisteredBound = (): void => this._updateEnv();

  private static _styleInjected = false;

  static readonly observedAttributes = ['no-transfer-button'];

  constructor() {
    super();
  }

  attributeChangedCallback(name: string, _old: string | null, next: string | null): void {
    if (name === 'no-transfer-button' && this.transferButton) {
      this.transferButton.style.display = next !== null ? 'none' : '';
    }
  }

  connectedCallback(): void {
    if (!AsExportGroup._styleInjected) {
      const style = document.createElement('style');
      style.textContent = styleText;
      style.setAttribute('data-component', 'as-export-group');
      document.head.appendChild(style);
      AsExportGroup._styleInjected = true;
    }
    if (!this._built) this._build();
    this._downloadWiring ??= wireDownloadButton({
      button: this.downloadButton!,
      status: () => this.downloadStatus as AsOpStatus | null,
      getExporter: () => this.exporter,
      getFilename: () => this.packageName ? `${this.packageName}.zip` : undefined,
      getExporterId: () => this.exporter?.exporterId ?? 'default',
    });
    this._removeProjectEffect = internalData.subscribeKey('currentProject', () => this._syncDefaultName());
    // appId can be set after this element connects, so re-sync the default name
    // once it arrives — otherwise the untitled default lacks the app suffix.
    this._removeAppIdEffect = internalData.subscribeKey('appId', () => this._syncDefaultName());
    this._removeCanDownloadEffect = internalData.subscribeKey('canDownload', () => this._syncDownloadEnabled());
    this._syncDefaultName();
    this._syncDownloadEnabled();
    // Reset on a new session (broadcast by createAndSetNewProject).
    window.addEventListener('as-inputs-reset', this._onResetBound);
    // A plugin can register its button descriptor at any point relative to this
    // element connecting (see ExportBuilder.registerButton()'s doc comment) —
    // re-sync directly off that signal rather than relying only on the env
    // attribute happening to change afterward. 'load' is an unconditional
    // catch-all: it fires strictly after every module script has finished
    // executing, so a registration missed by both the immediate build and the
    // event listener (whatever the exact cause in a given app's bundle) is
    // still guaranteed to be picked up once the page has fully loaded.
    window.addEventListener('as-export-descriptor-registered', this._onDescriptorRegisteredBound);
    window.addEventListener('load', this._onDescriptorRegisteredBound, { once: true });
    const app = asApp();
    if (app) {
      this._observer = new MutationObserver(() => this._updateEnv());
      this._observer.observe(app, { attributes: true, attributeFilter: ['env'] });
    }
    this._updateEnv();
  }

  disconnectedCallback(): void {
    this._downloadWiring?.dispose();
    this._downloadWiring = null;
    this._observer?.disconnect();
    this._observer = null;
    this._removeProjectEffect?.();
    this._removeProjectEffect = null;
    this._removeAppIdEffect?.();
    this._removeAppIdEffect = null;
    this._removeCanDownloadEffect?.();
    this._removeCanDownloadEffect = null;
    window.removeEventListener('as-inputs-reset', this._onResetBound);
    window.removeEventListener('as-export-descriptor-registered', this._onDescriptorRegisteredBound);
    window.removeEventListener('load', this._onDescriptorRegisteredBound);
  }

  /**
   * Restore the step to its just-loaded state on a new session: drop the
   * user-edited-name flag so the default filename re-derives, re-sync that name,
   * and clear any lingering download/transfer button statuses.
   */
  private _handleSessionReset(): void {
    this._nameUserEdited = false;
    this._syncDefaultName();
    this.reset();
  }

  // ── Public API ────────────────────────────────────────────────────────────────

  /** Current format dropdown value; falls back to first saveFormats entry or 'stl'. */
  get format(): string {
    return (this._formatDropdownEl as any)?.value
      ?? this._config.saveFormats?.[0]?.value
      ?? DEFAULT_FORMATS[0].value;
  }

  /** Explicit ExportBuilder to use; falls back to as-app.exporter unless no-default-exporter is set. */
  get exporter(): ExportBuilder | null {
    if (this._exporter) return this._exporter;
    if (this.hasAttribute('no-default-exporter')) return null;
    return (asApp() as any)?.exporter ?? null;
  }

  set exporter(v: ExportBuilder | null) {
    this._exporter = v;
  }

  /** Live map of all registered scope entries, keyed by scope key. outputPart is a backwards-compat alias for trcadMesh. */
  get scopes(): Record<string, ExportScope> {
    const map = Object.fromEntries(
      [...this._scopeMap.entries()].map(([k, acc]) => [k, this._toExternalScope(acc)])
    );
    if ('trcadMesh' in map && !('outputPart' in map)) map['outputPart'] = map['trcadMesh'];
    return map;
  }

  /** @deprecated Use `scopes` instead. */
  get exportScopes(): Record<string, ExportScope> {
    console.warn('as-export-group: exportScopes is deprecated — use scopes');
    return this.scopes;
  }

  /** Returns true if a scope with the given key is registered. outputPart is a backwards-compat alias for trcadMesh. */
  hasKey(key: string): boolean {
    return this._scopeMap.has(this._resolveKey(key));
  }

  private _resolveKey(key: string): string {
    return key === 'outputPart' ? 'trcadMesh' : key;
  }

  private _toExternalScope(acc: ExportScopeConfigAccessor): ExportScope {
    return {
      get key()              { return acc.key; },
      get defaultLabelText() { return acc.defaultLabelText; },
      set defaultLabelText(v: string) { acc.defaultLabelText = v; },
      get checked()          { return acc.checked; },
      set checked(v: boolean) { acc.checked = v; },
      get disabled()         { return acc.disabled; },
      get visible()          { return acc.visible; },
      set visible(v: boolean) { acc.visible = v; },
      get fileCfgs()         { return acc.fileCfgs; },
      set fileCfgs(v: FileDataSchema[]) { acc.fileCfgs = v; },
      get el()               { return acc.el; },
    };
  }

  /** Get the current package name: reflects live user edits to the filename input, falling back to the configured value. */
  get packageName(): string | undefined {
    const live = (this._filenameInputEl as any)?.value;
    return live != null && live !== '' ? live : this._config.packageName;
  }

  /** Set the package name: updates the filename input, download filename, and GrabCAD package-name attribute. */
  set packageName(v: string) {
    this._config.packageName = v;
    if (this._filenameInputEl) this._filenameInputEl.setAttribute('value', v);
    this._applyPackageNameToButtons(v);
  }

  /**
   * State of the STEP checkbox in the "Auxiliary files" hovercard. When true,
   * every checked scope (unless it opted out via noStepChild) also exports its
   * matching STEP files at the next export.
   */
  get includeStepFiles(): boolean {
    return (this._auxStepCheckboxEl as any)?.checked ?? false;
  }

  set includeStepFiles(v: boolean) {
    if (this._auxStepCheckboxEl) (this._auxStepCheckboxEl as any).checked = v;
  }

  /** Override the format dropdown options (default: STL, 3MF). */
  set saveFormats(v: FormatOption[]) {
    this._config.saveFormats = v;
    this._syncFormatOptions();
  }

  /** Add a top-level scope checkbox. Chainable. */
  addExportScope(item: ExportScopeConfig): this {
    if (this._built && this._scopeGroupEl) {
      this._addExportScopeConfig(item, this._scopeGroupEl);
      this._updateSingleScopeState();
    } else {
      this._config.scopeItems = [...(this._config.scopeItems ?? []), item];
    }
    return this;
  }

  /**
   * Add an indented child scope under an existing scope. The child participates in the
   * parent's enable/disable behaviour and is folded into the parent's FileDataSchema.children
   * at export time. Chainable.
   */
  addChildScope(parentKey: string, child: ExportScopeConfig): this {
    const parentAccessor = this._scopeMap.get(this._resolveKey(parentKey));
    if (!parentAccessor?.el) return this;

    const childCb = this._scopeCheckbox(child, true);
    if (!parentAccessor.checked) childCb.setAttribute('disabled', '');
    if (!parentAccessor.visible) childCb.style.display = 'none';

    const childAccessor = new ExportScopeConfigAccessor(child.key, child.defaultLabelText, childCb, child.fileCfgs);
    this._scopeMap.set(child.key, childAccessor);

    const existingChildKeys = parentAccessor._childKeys;
    const insertAfter = existingChildKeys.length > 0
      ? this._scopeMap.get(existingChildKeys[existingChildKeys.length - 1])?.el ?? parentAccessor.el
      : parentAccessor.el;
    insertAfter!.insertAdjacentElement('afterend', childCb);

    parentAccessor._addChildKey(child.key);
    childCb.addEventListener('as-input-change', () => this._rebuildExporter());
    return this;
  }

  /** Remove a scope checkbox and all its registered children by key. Chainable. */
  removeExportScope(key: string): this {
    const resolved = this._resolveKey(key);
    const accessor = this._scopeMap.get(resolved);
    if (!accessor) return this;
    for (const childKey of accessor._childKeys) {
      this._scopeMap.get(childKey)?.el?.remove();
      this._scopeMap.delete(childKey);
    }
    accessor.el?.remove();
    this._scopeMap.delete(resolved);
    this._updateSingleScopeState();
    return this;
  }

  /** Bulk-set configuration; builds the DOM if not yet connected, otherwise applies incrementally. */
  setFromObject(config: ExportGroupConfig): void {
    if (config.exporter !== undefined) this._exporter = config.exporter;
    Object.assign(this._config, config);
    if (!this.isConnected) return;
    if (!this._built) {
      this._build();
    } else {
      this._applyConfig();
    }
  }

  reset(): void {
    this._downloadWiring?.reset();
    this._gcBtnEntries.forEach(({ el }) => (el as any)?.reset?.());
  }

  // ── Private ───────────────────────────────────────────────────────────────────

  /** Parse an HTML string into a DocumentFragment via a detached <template> element. */
  private static _html(str: string): DocumentFragment {
    const tpl = document.createElement('template');
    tpl.innerHTML = str;
    return tpl.content;
  }

  /** Show/hide a scope's child checkboxes along with the scope itself. */
  private _syncChildVisibility(accessor: ExportScopeConfigAccessor): void {
    const display = accessor.visible ? '' : 'none';
    for (const childKey of accessor._childKeys) {
      const el = this._scopeMap.get(childKey)?.el;
      if (el) el.style.display = display;
    }
  }

  /** If exactly one top-level scope is visible, force it checked + disabled; otherwise restore all to enabled. */
  private _updateSingleScopeState(): void {
    const childKeySet = new Set<string>();
    for (const [, acc] of this._scopeMap) {
      for (const key of acc._childKeys) childKeySet.add(key);
    }
    const visible = [...this._scopeMap.values()].filter(a => !childKeySet.has(a.key) && a.visible);
    const solo = visible.length === 1;
    for (const a of visible) {
      if (solo) {
        a.checked = true;
        a.el?.setAttribute('disabled', '');
      } else {
        a.el?.removeAttribute('disabled');
      }
    }
  }

  /** First-time DOM construction: creates both panels and appends them in order. */
  private _build(): void {
    this._built = true;
    this.appendChild(this._buildActionsPanel());
    this.appendChild(this._buildScopePanel());
    this._updateSingleScopeState();
    this._updateEnv();
  }

  /** Build the Export panel: download button, settings cog hovercard, GrabCAD buttons with the auxiliary-files cog, and transfer button. */
  private _buildActionsPanel(): HTMLElement {
    const frag = AsExportGroup._html(/*html*/`
      <as-param-group subheading-default-text="Export" subheading-dict-key="fileIO.export.heading">
        <div class="export-actions-row">
          <div class="export-save-row">
            <as-button
              name="export-all"
              default-text="Save file"
              dict-key="fileIO.export.saveButton"
              no-default-icon
              tooltip-default-text="${SAVE_TOOLTIP}"
              tooltip-dict-key="fileIO.export.saveTooltip"
              tooltip-placement="right">
            </as-button>
            <as-op-status class="download-status"></as-op-status>
            <as-hovercard-wrapper id="export-settings-btn" class="export-settings-toggle"
              toggle-on-click show-on-hover="false" placement="right-bottom" offset="12">
              <as-icon-button class="export-settings-cog"
                size="s" icon-set="settings"
                black toggle name="toggle-export-file-settings"
                tooltip-default-text="Save file settings" tooltip-dict-key="fileIO.export.settingsHeading" tooltip-placement="right">
              </as-icon-button>
              <as-hovercard class="export-settings-hovercard">
                <div class="export-settings-content">
                  <p class="export-settings-heading"><as-text dict-key="fileIO.export.settingsHeading">Save file settings</as-text></p>
                  <as-string-input ignore-reset
                    label-default-text="Name"
                    label-dict-key="fileIO.export.nameLabel"
                    placeholder="Enter a file name"
                    help-tooltip-default-text="Choose the name for the exported package."
                    help-tooltip-dict-key="fileIO.export.nameTooltip">
                  </as-string-input>
                  <as-dropdown ignore-reset
                    label-default-text="Model format"
                    label-dict-key="fileIO.export.formatLabel"
                    help-tooltip-default-text="Choose the file format for local saving."
                    help-tooltip-dict-key="fileIO.export.formatTooltip">
                  </as-dropdown>
                  <div class="export-settings-ok-row">
                    <as-button id="export-settings-ok-btn" default-text="OK" dict-key="fileIO.export.ok" no-default-icon></as-button>
                  </div>
                </div>
              </as-hovercard>
            </as-hovercard-wrapper>
          </div>
          <div class="export-grabcad-row">
            <as-hovercard-wrapper id="export-aux-btn" class="export-settings-toggle"
              toggle-on-click show-on-hover="false" placement="right-bottom" offset="12">
              <as-icon-button class="export-settings-cog"
                size="s" icon-set="settings"
                black toggle name="toggle-export-aux-files"
                tooltip-default-text="Auxiliary files" tooltip-dict-key="fileIO.export.auxHeading" tooltip-placement="right">
              </as-icon-button>
              <as-hovercard class="export-settings-hovercard">
                <div class="export-settings-content">
                  <p class="export-settings-heading"><as-text dict-key="fileIO.export.auxHeading">Auxiliary files</as-text></p>
                  <as-checkbox name="include-step-files" ignore-reset
                    label-default-text="STEP"
                    label-dict-key="fileIO.export.auxStep">
                  </as-checkbox>
                </div>
              </as-hovercard>
            </as-hovercard-wrapper>
          </div>
          <as-button
            default-text="Send to other apps…"
            dict-key="fileIO.export.transferButton"
            name="transfer-mesh"
            no-default-icon
            tooltip-default-text="Send the selected parts to another app in Additive App Suite for the next step."
            tooltip-dict-key="fileIO.export.transferTooltip"
            tooltip-placement="top">
          </as-button>
        </div>
      </as-param-group>
    `);

    const group             = frag.querySelector('as-param-group') as HTMLElement;
    const row               = group.querySelector('div') as HTMLElement;
    this._saveRowEl         = group.querySelector('.export-save-row') as HTMLElement;
    this.downloadButton     = group.querySelector('as-button[name="export-all"]') as HTMLElement;
    this.downloadStatus     = group.querySelector('.download-status') as HTMLElement;
    this._filenameInputEl   = group.querySelector('as-string-input') as HTMLElement;
    this._formatDropdownEl  = group.querySelector('as-dropdown') as HTMLElement;
    this._exportSettingsButton  = group.querySelector('#export-settings-btn') as HTMLElement;
    this.transferButton     = group.querySelector('as-button[name="transfer-mesh"]') as HTMLElement;
    const cogBtn            = this._exportSettingsButton.querySelector('as-icon-button') as HTMLElement;
    const hovercard         = this._exportSettingsButton.querySelector('as-hovercard') as HTMLElement;
    const hovercardWrapper  = this._exportSettingsButton;
    const okBtn             = group.querySelector('#export-settings-ok-btn') as HTMLElement;
    this._gcRowEl           = group.querySelector('.export-grabcad-row') as HTMLElement;
    this._auxSettingsWrapper = group.querySelector('#export-aux-btn') as HTMLElement;
    this._auxStepCheckboxEl = this._auxSettingsWrapper.querySelector('as-checkbox[name="include-step-files"]') as HTMLElement;
    // Ids of their own, so none takes a generic one like "name" from its label.
    this._filenameInputEl.id = this._childId('name');
    this._formatDropdownEl.id = this._childId('format');
    this._auxStepCheckboxEl.id = this._childId('step-files');
    const auxCogBtn         = this._auxSettingsWrapper.querySelector('as-icon-button') as HTMLElement;

    if (this._config.packageName) {
      this._filenameInputEl.setAttribute('value', this._config.packageName);
    }
    if (this.hasAttribute('no-transfer-button')) this.transferButton.style.display = 'none';

    this.downloadButton.addEventListener('as-button-click', () => this._rebuildExporter());
    hovercardWrapper.addEventListener('as-hovercard-hide', () => {
      (cogBtn as any).forceToggleState?.(false);
    });
    this._auxSettingsWrapper.addEventListener('as-hovercard-hide', () => {
      (auxCogBtn as any).forceToggleState?.(false);
    });
    this._auxStepCheckboxEl.addEventListener('as-input-change', () => this._rebuildExporter());
    this._filenameInputEl.addEventListener('as-input-change', () => {
      if (!this._updatingName) this._nameUserEdited = true;
    });
    okBtn.addEventListener('as-button-click', () => {
      const name = (this._filenameInputEl as any)?.value ?? '';
      this._applyPackageNameToButtons(name);
      if (name) this._nameUserEdited = true;
      sendEv(hovercard, 'as-hovercard-close');
    });

    this._syncFormatOptions();

    this._gcBtnEntries = [];
    this._actionsRowEl = row;
    this._syncPluginButtons();

    return group;
  }

  /**
   * Create a DOM element for any registered plugin button descriptor (e.g.
   * GrabCAD) not yet represented in #gcBtnEntries. Idempotent — safe to call
   * repeatedly. Plugins register asynchronously relative to this element:
   * env detection (which decides whether a requiredEnv button is even
   * relevant) resolves off an async getUserData() call, so a plugin's
   * registerButton() can still be pending the first time this element
   * connects and builds. Re-run from _updateEnv() (which already re-fires on
   * every as-app[env] change) so a late-registering plugin's button still
   * gets created once its descriptor shows up, instead of being permanently
   * missing from whatever ExportBuilder.getButtonDescriptors() returned at
   * first build.
   */
  private _syncPluginButtons(): void {
    const row = this._actionsRowEl;
    if (!row) return;
    const known = new Set(this._gcBtnEntries.map(({ el }) => el.tagName.toLowerCase()));
    for (const desc of ExportBuilder.getButtonDescriptors()) {
      if (known.has(desc.tagName.toLowerCase())) continue;
      const btn = document.createElement(desc.tagName);
      for (const [attr, val] of Object.entries(desc.defaultAttrs ?? {})) btn.setAttribute(attr, val);
      const extraAttrs = desc.extraAttrs ?? [];
      // this.packageName (not #config.packageName) so a late-registering button
      // still picks up the current auto-computed name, not just an explicit override.
      if (extraAttrs.includes('package-name') && this.packageName) btn.setAttribute('package-name', this.packageName);
      btn.addEventListener('as-button-click', () => this._rebuildExporter());
      this._gcBtnEntries.push({ el: btn, requiredEnv: desc.requiredEnv, extraAttrs });
      // Into the GrabCAD row, before the auxiliary-files cog, so the cog sits
      // next to the transfer button like the save row's settings cog does.
      if (this._gcRowEl && this._auxSettingsWrapper) {
        this._gcRowEl.insertBefore(btn, this._auxSettingsWrapper);
      } else {
        row.insertBefore(btn, this.transferButton);
      }
    }
  }

  /** Build the Export scope panel: collapsible accordion containing scope checkboxes. */
  private _buildScopePanel(): HTMLElement {
    const frag = AsExportGroup._html(/*html*/`
      <as-param-group class="param-group-collapsible">
        <as-accordion header-default-text="Export scope" header-dict-key="fileIO.export.scopeHeading">
          <div data-scope-content>
            <p class="export-scope-desc"><as-text dict-key="fileIO.export.scopeDescription">Choose what to include in your export.</as-text></p>
          </div>
        </as-accordion>
      </as-param-group>
    `);

    const panel   = frag.querySelector('as-param-group') as HTMLElement;
    const content = panel.querySelector('[data-scope-content]') as HTMLElement;
    this._scopeGroupEl = content;

    if (!this.hasAttribute('no-default-scopes')) {
      for (const item of DEFAULT_SCOPES) this._addExportScopeConfig(item, content);
    }
    for (const item of (this._config.scopeItems ?? [])) {
      if (!this._scopeMap.has(item.key)) this._addExportScopeConfig(item, content);
    }

    return panel;
  }

  /** The id of one of this group's own controls. */
  private _childId(part: string): string {
    return `${this.id || 'export-group'}-${part}`;
  }

  /** A scope's checkbox. Like the settings controls it is chrome, not project state. */
  private _scopeCheckbox(cfg: ExportScopeConfig, child = false): HTMLElement {
    const cb = document.createElement('as-checkbox');
    cb.id = this._childId(`scope-${cfg.key}`);
    cb.setAttribute('ignore-reset', '');
    cb.setAttribute('label-default-text', cfg.defaultLabelText);
    if (cfg.labelDictKey) cb.setAttribute('label-dict-key', cfg.labelDictKey);
    cb.setAttribute('name', cfg.key);
    if (child) cb.classList.add('export-scope-child');
    if (cfg.checked !== false) cb.setAttribute('checked', '');
    return cb;
  }

  /** Render a scope checkbox (and any declared children) into container and register them in #scopeMap. */
  private _addExportScopeConfig(item: ExportScopeConfig, container: HTMLElement): void {
    const cb = this._scopeCheckbox(item);

    const accessor = new ExportScopeConfigAccessor(item.key, item.defaultLabelText, cb, item.fileCfgs, item.visible !== false);
    accessor.noStepChild = item.noStepChild === true;
    this._scopeMap.set(item.key, accessor);
    container.appendChild(cb);

    const childKeys: string[] = [];
    for (const child of (item.children ?? [])) {
      const childCb = this._scopeCheckbox(child, true);

      const childAccessor = new ExportScopeConfigAccessor(child.key, child.defaultLabelText, childCb, child.fileCfgs);
      this._scopeMap.set(child.key, childAccessor);
      childKeys.push(child.key);
      container.appendChild(childCb);

      childCb.addEventListener('as-input-change', () => this._rebuildExporter());
    }

    accessor._setChildKeys(childKeys);
    this._syncChildVisibility(accessor);
    accessor._setOnVisibilityChange(() => {
      this._syncChildVisibility(accessor);
      this._updateSingleScopeState();
    });

    cb.addEventListener('as-input-change', () => {
      const checked = (cb as any).checked;
      for (const childKey of accessor._childKeys) {
        const childEl = this._scopeMap.get(childKey)?.el;
        if (childEl) {
          if (checked) childEl.removeAttribute('disabled');
          else childEl.setAttribute('disabled', '');
        }
      }
      this._rebuildExporter();
    });
  }

  /** Repopulate the format dropdown with the current saveFormats list (or DEFAULT_FORMATS). */
  private _syncFormatOptions(): void {
    if (!this._formatDropdownEl) return;
    this._formatDropdownEl.innerHTML = '';
    for (const fmt of (this._config.saveFormats ?? DEFAULT_FORMATS)) {
      const opt = document.createElement('as-option');
      opt.setAttribute('value', fmt.value);
      opt.textContent = fmt.label;
      this._formatDropdownEl.appendChild(opt);
    }
  }

  /** Incrementally apply #config to the already-built DOM (called by setFromObject after first render). */
  private _applyConfig(): void {
    if (this._config.packageName != null) this.packageName = this._config.packageName;
    this._syncFormatOptions();
    for (const item of (this._config.scopeItems ?? [])) {
      if (!this._scopeMap.has(item.key)) this._addExportScopeConfig(item, this._scopeGroupEl!);
    }
    this._updateEnv();
  }

  /** Re-populate the ExportBuilder from the current checkbox state and selected format. Called on any scope or format change. */
  private _rebuildExporter(): void {
    const exp = this.exporter;
    if (!exp) return;
    exp.clear();
    const fmt = this.format;
    if (fmt) try { exp.preferredMeshType = fmt as MeshType; } catch { /* non-mesh format */ }

    const childKeySet = new Set<string>();
    for (const [, accessor] of this._scopeMap) {
      for (const key of accessor._childKeys) childKeySet.add(key);
    }

    const includeStep = this.includeStepFiles;

    for (const [key, accessor] of this._scopeMap) {
      if (childKeySet.has(key) || !accessor.checked || accessor.fileCfgs.length === 0) continue;

      // Child scopes (addChildScope) keep the legacy behaviour of bundling
      // onto the scope's first part — there's no per-part signal for those.
      const childCfgs: FileDataSchema[] = [];
      for (const childKey of accessor._childKeys) {
        const child = this._scopeMap.get(childKey);
        if (child?.checked && !child.disabled) childCfgs.push(...child.fileCfgs);
      }

      // The auxiliary STEP files are matched to their own part instead, since
      // a multi-part scope's STEP files must not all end up as dependencies of
      // the first part.
      const autoStepByPart = (includeStep && !accessor.noStepChild)
        ? deriveStepFileCfgsByPart(accessor.fileCfgs)
        : null;

      accessor.fileCfgs.forEach((cfg, i) => {
        const extra = [
          ...(autoStepByPart?.get(cfg) ? [autoStepByPart.get(cfg)!] : []),
          ...(i === 0 ? childCfgs : []),
        ];
        exp.addFile(extra.length > 0 ? { ...cfg, children: [...(cfg.children ?? []), ...extra] } : cfg);
      });
    }
  }

  /**
   * Once the user has a real project name, the file name is just that name — no
   * app suffix. Only the untitled/default case (no project name yet) gets one,
   * so the file is still identifiable as e.g. "Untitled_Clamping_Jaws" rather
   * than a bare "Untitled" that every app would produce.
   */
  private _computeDefaultName(): string {
    // Collapse whitespace runs to '_' so the derived file/package name is filename-safe.
    const safe = (s: string) => s.trim().replace(/\s+/g, '_');
    // A project still named Untitled has no name yet.
    const rawName = internalData.currentProject?.metadata.name ?? '';
    const projectName = rawName === 'Untitled' ? '' : safe(rawName);
    if (projectName) return projectName;
    // Prefer as-header's human-readable app-title over the raw appId (which
    // can be an internal slug/ID) — same source project-mixin uses for the
    // .asproject save filename.
    const prettyAppName = asHeader()?.getAttribute('app-title') || internalData.appId || '';
    return prettyAppName ? `Untitled_${safe(prettyAppName)}` : 'Untitled';
  }

  private _syncDefaultName(): void {
    const projectId = internalData.currentProject?.metadata.id ?? null;
    if (projectId !== this._activeProjectId) {
      this._activeProjectId = projectId;
      this._nameUserEdited = false;
    }
    if (this._nameUserEdited || this._config.packageName != null) return;
    const name = this._computeDefaultName();
    if (!name || !this._filenameInputEl) return;
    this._updatingName = true;
    this._filenameInputEl.setAttribute('value', name);
    this._updatingName = false;
    // Keep the GrabCAD button(s) in sync too — not just the settings-card input.
    // Needed because in envs where the whole save row (incl. the OK button that
    // otherwise does this) is hidden, e.g. grabcad, this auto-sync path is the
    // only place a post-connect project rename ever reaches the button.
    this._applyPackageNameToButtons(name);
  }

  /** Pushes name onto every registered GrabCAD-style button's package-name attribute. */
  private _applyPackageNameToButtons(name: string): void {
    for (const { el, extraAttrs } of this._gcBtnEntries) {
      if (extraAttrs.includes('package-name')) el.setAttribute('package-name', name);
    }
  }

  /**
   * Mirror internalData.canDownload (license-gated, seeded by as-app from the
   * appList's downloadEnabled flag) onto the Save button: disabled with an
   * explanatory tooltip when downloads are off.
   */
  private _syncDownloadEnabled(): void {
    const btn = this.downloadButton;
    if (!btn) return;
    const enabled = internalData.canDownload !== false;
    btn.toggleAttribute('disabled', !enabled);
    btn.setAttribute('tooltip-default-text', enabled ? SAVE_TOOLTIP : DOWNLOAD_DISABLED_TOOLTIP);
    btn.setAttribute('tooltip-dict-key', enabled ? 'fileIO.export.saveTooltip' : DOWNLOAD_DISABLED_TOOLTIP_DICT_KEY);
    this._exportSettingsButton?.classList.toggle('hidden', !enabled);
  }

  /** Show or hide env-gated buttons (e.g. GrabCAD) based on the current as-app[env] attribute.
   *  Also syncs in any plugin button that registered after this element's first build. */
  private _updateEnv(): void {
    this._syncPluginButtons();
    const env = asApp()?.getAttribute('env') ?? '';
    let anyPluginVisible = false;
    for (const { el, requiredEnv } of this._gcBtnEntries) {
      const visible = !(requiredEnv && env !== requiredEnv);
      el.style.display = visible ? '' : 'none';
      if (visible) anyPluginVisible = true;
    }
    // The auxiliary-files cog belongs to the transfer button next to it. With no
    // visible plugin button the whole row goes, rather than leaving a lone cog.
    if (this._gcRowEl) this._gcRowEl.style.display = anyPluginVisible ? '' : 'none';
    // Local "Save file" is hidden in the GrabCAD env — GrabCAD Print users
    // transfer to the build tray instead (see grabcadPlugin.ts).
    if (this._saveRowEl) this._saveRowEl.style.display = env === 'grabcad' ? 'none' : '';
  }
}

if (!customElements.get(AsExportGroup.tag)) customElements.define(AsExportGroup.tag, AsExportGroup);

// Deprecated alias tag: <as-export-step> keeps working but warns once per page.
if (!customElements.get('as-export-step')) {
  customElements.define('as-export-step', DeprecatedEl(
    AsExportGroup,
    'as-export-step',
    'as-export-group',
    'as-export-step is deprecated and will be removed in 0.18.0 — use as-export-group instead (they are the same component but -group has a more consistent name).',
  ));
}
